import axios from 'axios';

const API_BASE = process.env.REACT_APP_HOST_URL;

/**
 * CRM List Management API Service
 * 
 * Domain Logic:
 * - Integrates with CRM backend API for list view management
 * - Handles entity-specific list operations (contacts, accounts, opportunities)
 * - Manages user default list preferences per entity type
 * - Provides local caching for default lists to improve performance
 * - Supports list CRUD operations with proper error handling
 */

/**
 * Fetches all saved list views for a specific CRM entity type
 * Returns user's custom list configurations for the entity
 * 
 * @param {string} entityType - CRM entity type (contacts, accounts, opportunities)
 * @returns {Promise<Array>} List of saved views for the entity
 */
export const getLists = (entityType) => {
  return axios.get(`${API_BASE}/lists/${entityType}`)
    .then(res => res.data.data)
    .catch(err => { throw err; });
};

/**
 * Creates a new saved list view for a CRM entity
 * Initializes list with entity type and column configuration
 * 
 * @param {Object} data - List configuration data
 * @param {string} data.entity_type - CRM entity type
 * @param {string} data.list_name - Display name for the list
 * @param {string} data.list_key - API identifier for the list
 * @param {string} data.fields_json - JSON string of column configuration
 * @returns {Promise<Object>} Created list data
 */
export const createList = (data) => {
  return axios.post(`${API_BASE}/lists`, data)
    .then(res => res.data)
    .catch(err => { throw err; });
};

/**
 * Updates an existing saved list view
 * Modifies list name, column configuration, or visibility settings
 * 
 * @param {string} id - List ID to update
 * @param {Object} data - Updated list configuration
 * @returns {Promise<Object>} Updated list data
 */
export const updateList = (id, data) => {
  return axios.put(`${API_BASE}/lists/${id}`, data)
    .then(res => res.data)
    .catch(err => { throw err; });
};

/**
 * Deletes a saved list view
 * Removes the list and its configuration from the system
 * 
 * @param {string} id - List ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteList = (id) => {
  return axios.delete(`${API_BASE}/lists/${id}`)
    .then(res => res.data)
    .catch(err => { throw err; });
};

/**
 * Sets the default list view for a specific CRM entity type
 * Updates user preference for which list to show by default
 * 
 * @param {string} entityType - CRM entity type
 * @param {string} listId - List ID to set as default
 * @returns {Promise<Object>} Default list confirmation
 */
export const setDefaultList = (entityType, listId) => {
  return axios.post(`${API_BASE}/lists/default`, {
    entity_type: entityType,
    list_id: listId
  });
};

/**
 * Fetches all default lists for the current user across all entity types
 * Caches results in localStorage for improved performance
 * 
 * @returns {Promise<Array>} Default list configurations for all entities
 */
export const getDefaultLists = async () => {
  try {
    const response = await axios.get(`${API_BASE}/lists/default`);
    localStorage.setItem('defaultLists', JSON.stringify(response.data.data));
    return response.data.data;
  } catch (error) {
    console.error('Error fetching default lists:', error);
    throw error;
  }
};

/**
 * Fetches the default list for a specific CRM entity type
 * Returns user's preferred list view for the entity
 * 
 * @param {string} entityType - CRM entity type
 * @returns {Promise<Object>} Default list configuration for the entity
 */
export const getDefaultList = async (entityType) => {
  try {
    const response = await axios.get(`${API_BASE}/lists/default/${entityType}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching default list for ${entityType}:`, error);
    throw error;
  }
}; 